/**
 * Opens one custom view: loads it, asks the person to allow its data the first time (or after a
 * revision added a source), fetches only the approved data, and renders it inline or full
 * screen in {@link FluxViewFrame}.
 *
 * @module apps/web/src/components/flux-agent/views/FluxViewOpener
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Maximize2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  VIEW_SOURCE_LABELS,
  fluxViewsApi,
  type FluxView,
  type FluxViewData,
} from '@/lib/fluxViewsApi';
import { FluxViewFrame } from './FluxViewFrame';

/** Inputs for {@link FluxViewOpener}. */
export interface FluxViewOpenerProps {
  /** Server id of the view to open. */
  viewId: string;
  /** Called with a view's suggested message when the person moves it to the composer. */
  onSuggest?: (text: string) => void;
}

/** What the opener is showing. */
type Stage =
  | { /** Loading the owned view. */ kind: 'loading' }
  | {
      /** Request or revision validation failed. */ kind: 'error';
      /** User-visible failure. */ message: string;
    }
  | {
      /** Waiting for exact-source consent. */ kind: 'consent';
      /** Revision whose sources are displayed. */ view: FluxView;
    }
  | {
      /** Approved snapshot is ready to mount. */ kind: 'ready';
      /** Approved view revision. */ view: FluxView;
      /** Matching revision snapshot, or null for a data-free view. */ data: FluxViewData | null;
    };

/** Load, ask consent for, and show one view. */
export function FluxViewOpener({ viewId, onSuggest }: FluxViewOpenerProps) {
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const show = useCallback(async (view: FluxView) => {
    if (!view.access.granted) {
      setStage({ kind: 'consent', view });
      return;
    }
    if (!view.sources.length) {
      setStage({ kind: 'ready', view, data: null });
      return;
    }
    const data = await fluxViewsApi.getData(view.id, view.revision);
    if (!data.ok) {
      setStage({ kind: 'error', message: data.message });
      return;
    }
    if (data.data.revision !== view.revision) {
      setStage({
        kind: 'error',
        message: 'This view changed since it was opened. Reopen it and try again.',
      });
      return;
    }
    setStage({ kind: 'ready', view, data: data.data });
  }, []);

  useEffect(() => {
    let live = true;
    void fluxViewsApi.getView(viewId).then(result => {
      if (!live) return;
      if (!result.ok) setStage({ kind: 'error', message: result.message });
      else void show(result.data.view);
    });
    return () => {
      live = false;
    };
  }, [viewId, show]);

  const allow = async (view: FluxView) => {
    setBusy(true);
    const result = await fluxViewsApi.setAccess(view.id, view.revision, true, view.sources);
    setBusy(false);
    if (!result.ok) {
      setStage({ kind: 'error', message: result.message });
      return;
    }
    await show(result.data.view);
  };

  if (stage.kind === 'loading')
    return (
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs" role="status">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Opening the view…
      </p>
    );
  if (stage.kind === 'error')
    return (
      <p className="text-destructive text-xs" role="alert">
        {stage.message}
      </p>
    );
  if (stage.kind === 'consent')
    return (
      <div className="rounded-md border px-3 py-2 text-xs" data-testid="flux-view-consent">
        <p className="flex items-center gap-1.5 font-medium">
          <ShieldCheck className="text-primary h-3.5 w-3.5" aria-hidden="true" />
          This view wants to read
        </p>
        <ul className="text-muted-foreground mt-1 list-disc pl-5">
          {stage.view.sources.map(source => (
            <li key={source}>{VIEW_SOURCE_LABELS[source] || source}</li>
          ))}
        </ul>
        <p className="text-muted-foreground mt-1">
          Flux Agent wrote this view. It runs in a sealed frame with no internet access and cannot
          change anything; it only shows this data to you.
        </p>
        <div className="mt-2 flex gap-2">
          <Button size="sm" className="h-7" disabled={busy} onClick={() => void allow(stage.view)}>
            {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />}
            Allow and open
          </Button>
        </div>
      </div>
    );
  const { view, data } = stage;
  const frame = (fill: boolean) => (
    <FluxViewFrame
      key={`${view.id}:${view.revision}:${data?.generatedAt ?? ''}`}
      title={view.title}
      source={view.source || ''}
      data={data?.data ?? {}}
      fill={fill}
      revealOnReady
      onSuggest={
        onSuggest &&
        (text => {
          setFullScreen(false);
          onSuggest(text);
        })
      }
    />
  );
  return (
    <div className="space-y-1.5" data-testid="flux-view-open">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-[11px]">
          {data?.truncated ? 'Showing part of your data. ' : ''}
          Read-only · sealed frame
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={() => setFullScreen(true)}
        >
          <Maximize2 className="h-3 w-3" aria-hidden="true" /> Full screen
        </Button>
      </div>
      {!fullScreen && frame(false)}
      <Dialog open={fullScreen} onOpenChange={setFullScreen}>
        <DialogContent className="flex h-[90vh] max-w-5xl flex-col">
          <DialogHeader>
            <DialogTitle>{view.title}</DialogTitle>
            <DialogDescription>{view.purpose}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1">{fullScreen && frame(true)}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
